import { useQuery } from '@tanstack/react-query';

import { mispickApi } from 'api/warehouse/mispick';
import { ApiError } from 'shared/errors/ApiError';

export const UNPICKED_MISPICK_ITEMS_QUERY_KEY = 'unpickedMispickItems';

export const unpickedMispickItemsQueryKeys = {
    all: [UNPICKED_MISPICK_ITEMS_QUERY_KEY],
    byShipmentId: (shipmentId, facilityId) => [UNPICKED_MISPICK_ITEMS_QUERY_KEY, shipmentId, facilityId],
};

/**
 * Fetches unpicked items for a shipment that has a prior mispick end-of-line event.
 *
 * @param {Object} options
 * @param {number|string|null|undefined} options.shipmentId
 * @param {number|string|null|undefined} options.facilityId
 * @param {boolean} [options.enabled=true]
 * @returns {import('@tanstack/react-query').UseQueryResult<Object, ApiError>}
 */
export const useUnpickedMispickItemsQuery = ({ enabled = true, facilityId, shipmentId } = {}) =>
    useQuery({
        queryKey: unpickedMispickItemsQueryKeys.byShipmentId(shipmentId, facilityId),
        enabled: Boolean(enabled && shipmentId && facilityId),
        // Expected 400s (no mispick event, wrong facility, not found) are common for this lookup.
        retry: false,
        queryFn: async ({ signal }) => {
            const response = await mispickApi.getUnpickedItems(shipmentId, { facilityId, signal });
            if (!response?.status) {
                throw new ApiError(
                    response?.msg ?? 'Failed to fetch unpicked mispick items',
                    response?.statusCode,
                    response?.data,
                );
            }
            return response.data;
        },
    });
